import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactElement,
} from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Paper from "@mui/material/Paper";
import LinearProgress from "@mui/material/LinearProgress";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { getFunctions, httpsCallable } from "firebase/functions";
import { JarvisTheme } from "../../theme/jarvisTheme";

type TimeWindowKey = "last24h" | "last30d";

type UsageSnapshot = {
  generatedAt: string;
  projectId: string;
  bucket: string | null;
  windows: Record<TimeWindowKey, { start: string; end: string }>;
  functions: {
    invocations: {
      last30d: number | null;
    };
    errors: Record<TimeWindowKey, number | null>;
    latencyP95Ms: Record<TimeWindowKey, number | null>;
    quota: {
      monthlyInvocationsCap: number;
      monthlyInvocationsUsed: number | null;
      monthlyInvocationsPct: number | null;
    };
    notes: string[];
  };
  firestore: {
    reads: Record<TimeWindowKey, number | null>;
    notes: string[];
  };
  hosting: {
    bandwidthBytes: Record<TimeWindowKey, number | null>;
    monthly: {
      sentBytes: number | null;
      sentLimitBytes: number | null;
      sentPct: number | null;
    };
    notes: string[];
  };
  storage: {
    totalBytes: number | null;
    quota: {
      bytesCap: number;
      bytesUsed: number | null;
      bytesPct: number | null;
    };
    notes: string[];
  };
  gemini: {
    month: string;
    currency: "USD";
    totalEstimatedCostUsd: number;
    totalRequests: number;
    totalInputTokens: number;
    totalOutputTokens: number;
    byModel: Array<{
      model: string;
      requestCount: number;
      inputTokens: number;
      outputTokens: number;
      cachedTokens: number;
      estimatedCostUsd: number;
    }>;
    pricingNote: string;
    aiStudioUsageUrl: string;
    updatedAt: string | null;
    notes: string[];
  };
};

function fmtNumber(n: number | null | undefined): string {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  return n.toLocaleString();
}

function fmtMs(n: number | null | undefined): string {
  if (typeof n !== "number" || Number.isNaN(n)) return "—";
  if (n < 1000) return `${Math.round(n)} ms`;
  return `${(n / 1000).toFixed(2)} s`;
}

function fmtUsd(amount: number | null | undefined): string {
  if (typeof amount !== "number" || Number.isNaN(amount)) return "—";
  if (amount < 0.01 && amount > 0) return "< $0.01";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(amount);
}

function fmtBytes(bytes: number | null | undefined): string {
  if (typeof bytes !== "number" || Number.isNaN(bytes)) return "—";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(i === 0 ? 0 : 2)} ${units[i]}`;
}

function pctToProgress(pct: number | null | undefined): number | null {
  if (typeof pct !== "number" || Number.isNaN(pct)) return null;
  if (pct < 0) return 0;
  if (pct > 100) return 100;
  return pct;
}

/** Uncapped % of quota (for sort + alert border); null = no quota bar. */
function sortKeyFromUsagePct(raw: number | null | undefined): number {
  if (typeof raw !== "number" || Number.isNaN(raw)) return -1;
  return raw;
}

/** Green &lt;10%, yellow 10–50%, red &gt;50%; neutral when no quota. */
function quotaAlertBorder(rawUsagePct: number | null | undefined): {
  borderColor: string;
  borderWidth: number;
} {
  if (typeof rawUsagePct !== "number" || Number.isNaN(rawUsagePct)) {
    return { borderColor: JarvisTheme.colors.panelBorder, borderWidth: 1 };
  }
  if (rawUsagePct < 10) {
    return {
      borderColor: "rgba(52, 211, 153, 0.55)",
      borderWidth: 1,
    };
  }
  if (rawUsagePct <= 50) {
    return {
      borderColor: "rgba(250, 204, 21, 0.65)",
      borderWidth: 1,
    };
  }
  return { borderColor: "rgba(248, 113, 113, 0.75)", borderWidth: 1 };
}

/** Blaze no-cost included usage (same numbers we use for progress denominators). */
const BLAZE_NO_COST = {
  functionsInvocationsMonthly: 2_000_000,
  firestoreReadsDaily: 50_000,
  storageStoredBytes: 5 * 1024 * 1024 * 1024,
  hostingTransferDailyBytes: 360 * 1024 * 1024,
} as const;

function StatCard({
  title,
  value,
  subvalue,
  rawUsagePct,
  resetLabel,
}: {
  title: string;
  value: string;
  subvalue?: string;
  /** Uncapped % of quota; null = no progress bar (neutral border). */
  rawUsagePct?: number | null;
  resetLabel?: string;
}) {
  const barPct = pctToProgress(rawUsagePct);
  const { borderColor, borderWidth } = quotaAlertBorder(rawUsagePct);

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2,
        borderRadius: 1.25,
        border: `${borderWidth}px solid`,
        borderColor,
        bgcolor: JarvisTheme.colors.panelSoft,
        height: "100%",
        color: JarvisTheme.colors.text,
        boxSizing: "border-box",
        overflow: "hidden",
        transition: "border-color 0.25s ease",
      }}
    >
      <Typography
        sx={{
          fontFamily: JarvisTheme.fonts.ui,
          letterSpacing: "0.16em",
          textTransform: "uppercase",
          fontSize: "0.72rem",
          color: JarvisTheme.colors.muted,
          mb: 1,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontFamily: JarvisTheme.fonts.mono,
          fontSize: { xs: "1.2rem", sm: "1.35rem" },
          color: JarvisTheme.colors.text,
          mb: 0.5,
        }}
      >
        {value}
      </Typography>
      {subvalue && (
        <Typography
          sx={{
            color: JarvisTheme.colors.muted,
            fontSize: "0.82rem",
            minWidth: 0,
            overflowWrap: "anywhere",
          }}
        >
          {subvalue}
        </Typography>
      )}
      {typeof barPct === "number" && (
        <Box sx={{ mt: 1.5 }}>
          <LinearProgress
            variant="determinate"
            value={barPct}
            sx={{
              height: 8,
              borderRadius: 999,
              bgcolor: JarvisTheme.colors.accentTrack,
              "& .MuiLinearProgress-bar": {
                bgcolor: JarvisTheme.colors.accent,
              },
            }}
          />
          <Typography sx={{ mt: 0.5, opacity: 0.7, fontSize: "0.75rem" }}>
            {typeof rawUsagePct === "number" && !Number.isNaN(rawUsagePct)
              ? rawUsagePct.toFixed(1)
              : barPct.toFixed(1)}
            %
          </Typography>
        </Box>
      )}
      {resetLabel && (
        <Typography
          sx={{
            mt: 1,
            fontSize: "0.72rem",
            color: JarvisTheme.colors.muted,
            opacity: 0.55,
            fontFamily: JarvisTheme.fonts.mono,
          }}
        >
          ↺ {resetLabel}
        </Typography>
      )}
    </Paper>
  );
}

export function DashboardTab() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UsageSnapshot | null>(null);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const fn = httpsCallable<
        {
          bucket?: string;
          quotas?: {
            functionsMonthlyInvocations?: number;
            storageBytes?: number;
          };
        },
        UsageSnapshot
      >(getFunctions(), "getJarvisUsageSnapshot");
      const res = await fn({});
      setData(res.data ?? null);
    } catch (e: unknown) {
      setError(
        e instanceof Error
          ? e.message
          : "Failed to load usage snapshot. Ensure Functions are deployed and you're signed in as owner.",
      );
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  // Guard against React 18 dev StrictMode running effects twice.
  const didInitialLoad = useRef(false);
  useEffect(() => {
    if (didInitialLoad.current) return;
    didInitialLoad.current = true;
    void load();
  }, [load]);

  const derived = useMemo(() => {
    if (!data) return null;
    const invMonth = data.functions.quota.monthlyInvocationsUsed;
    const invCap = Math.min(
      data.functions.quota.monthlyInvocationsCap,
      BLAZE_NO_COST.functionsInvocationsMonthly,
    );
    const invPctRaw =
      typeof invMonth === "number" && invCap > 0
        ? (invMonth / invCap) * 100
        : typeof data.functions.quota.monthlyInvocationsPct === "number"
          ? data.functions.quota.monthlyInvocationsPct
          : null;

    const bytesUsed = data.storage.quota.bytesUsed;
    const bytesCap = Math.min(
      data.storage.quota.bytesCap,
      BLAZE_NO_COST.storageStoredBytes,
    );
    const bytesPctRaw =
      typeof bytesUsed === "number" && bytesCap > 0
        ? (bytesUsed / bytesCap) * 100
        : typeof data.storage.quota.bytesPct === "number"
          ? data.storage.quota.bytesPct
          : null;

    const hostingDaily = data.hosting.bandwidthBytes.last24h;
    const hostingCap = BLAZE_NO_COST.hostingTransferDailyBytes;
    const hostingPctRaw =
      typeof hostingDaily === "number" && hostingCap > 0
        ? (hostingDaily / hostingCap) * 100
        : null;

    const fsReads24h = data.firestore.reads.last24h;
    const fsReadsPctRaw =
      typeof fsReads24h === "number"
        ? (fsReads24h / BLAZE_NO_COST.firestoreReadsDaily) * 100
        : null;

    const now = new Date();

    // Time until next midnight PDT (UTC-7) — Firebase billing resets on Pacific time
    const PDT_OFFSET_MS = -7 * 60 * 60 * 1000;
    const nowPDT = new Date(now.getTime() + PDT_OFFSET_MS);
    const midnightPDT = new Date(nowPDT);
    midnightPDT.setUTCHours(24, 0, 0, 0);
    const msUntilMidnight = midnightPDT.getTime() - nowPDT.getTime();
    const hUntilMidnight = Math.floor(msUntilMidnight / 3_600_000);
    const mUntilMidnight = Math.floor((msUntilMidnight % 3_600_000) / 60_000);
    const midnightLabel =
      hUntilMidnight > 0
        ? `Resets at midnight PDT (in ${hUntilMidnight}h ${mUntilMidnight}m)`
        : `Resets at midnight PDT (in ${mUntilMidnight}m)`;

    // Days until end of month (in PDT)
    const lastDayOfMonthPDT = new Date(
      nowPDT.getUTCFullYear(),
      nowPDT.getUTCMonth() + 1,
      0,
    );
    const daysLeft = lastDayOfMonthPDT.getUTCDate() - nowPDT.getUTCDate();
    const monthResetLabel =
      daysLeft === 0
        ? "Resets tonight PDT (end of month)"
        : `Resets in ${daysLeft} day${
            daysLeft === 1 ? "" : "s"
          } (end of month PDT)`;

    return {
      invMonth,
      invCap,
      invPctRaw,
      bytesUsed,
      bytesCap,
      bytesPctRaw,
      hostingDaily,
      hostingPctRaw,
      hostingCap,
      fsReads24h,
      fsReads30d: data.firestore.reads.last30d,
      fsReadsPctRaw,
      midnightLabel,
      monthResetLabel,
    };
  }, [data]);

  const sortedStatTiles = useMemo(() => {
    if (!data || !derived) return [];

    const tiles: {
      id: string;
      sort: number;
      node: ReactElement;
    }[] = [
      {
        id: "functions",
        sort: sortKeyFromUsagePct(derived.invPctRaw),
        node: (
          <StatCard
            title="Function calls (30d)"
            value={fmtNumber(derived.invMonth ?? null)}
            subvalue={`No-cost: ${fmtNumber(BLAZE_NO_COST.functionsInvocationsMonthly)}/mo`}
            rawUsagePct={derived.invPctRaw}
            resetLabel={derived.monthResetLabel}
          />
        ),
      },
      {
        id: "firestore",
        sort: sortKeyFromUsagePct(derived.fsReadsPctRaw),
        node: (
          <StatCard
            title="Firestore reads (24h)"
            value={fmtNumber(derived.fsReads24h ?? null)}
            subvalue={`No-cost: ${fmtNumber(BLAZE_NO_COST.firestoreReadsDaily)}/day (Pacific) · 30d ${fmtNumber(derived.fsReads30d ?? null)}`}
            rawUsagePct={derived.fsReadsPctRaw}
            resetLabel={derived.midnightLabel}
          />
        ),
      },
      {
        id: "errors",
        sort: sortKeyFromUsagePct(null),
        node: (
          <StatCard
            title="Function errors (30d)"
            value={fmtNumber(data.functions.errors.last30d)}
            subvalue="No daily/monthly no-cost cap — signal only"
            resetLabel="Rolling 30-day window"
          />
        ),
      },
      {
        id: "latency",
        sort: sortKeyFromUsagePct(null),
        node: (
          <StatCard
            title="Latency p95 (24h)"
            value={fmtMs(data.functions.latencyP95Ms.last24h)}
            subvalue="No no-cost latency cap — signal only"
            resetLabel="Rolling 24h window"
          />
        ),
      },
      {
        id: "storage",
        sort: sortKeyFromUsagePct(derived.bytesPctRaw),
        node: (
          <StatCard
            title="Storage used"
            value={fmtBytes(derived.bytesUsed ?? null)}
            subvalue={`No-cost: ${fmtBytes(BLAZE_NO_COST.storageStoredBytes)} stored (default/modern bucket)`}
            rawUsagePct={derived.bytesPctRaw}
          />
        ),
      },
      {
        id: "hosting",
        sort: sortKeyFromUsagePct(derived.hostingPctRaw),
        node: (
          <StatCard
            title="Hosting bandwidth (24h)"
            value={fmtBytes(derived.hostingDaily ?? null)}
            subvalue={`No-cost: ${fmtBytes(BLAZE_NO_COST.hostingTransferDailyBytes)}/day egress · 10 GB site storage`}
            rawUsagePct={derived.hostingPctRaw}
            resetLabel={derived.midnightLabel}
          />
        ),
      },
    ];

    tiles.sort((a, b) =>
      b.sort !== a.sort ? b.sort - a.sort : a.id.localeCompare(b.id),
    );
    return tiles;
  }, [data, derived]);

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "center" },
          flexDirection: { xs: "column", sm: "row" },
          gap: 1,
          mb: 2,
        }}
      >
        <Box>
          <Typography
            sx={{
              fontFamily: JarvisTheme.fonts.ui,
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              fontSize: "0.8rem",
              color: JarvisTheme.colors.muted,
              mb: 0.5,
              opacity: 0.85,
            }}
          >
            Dashboard
          </Typography>
          <Typography
            sx={{
              opacity: 0.72,
              fontSize: "0.85rem",
              color: JarvisTheme.colors.muted,
            }}
          >
            Blaze: usage vs no-cost included quotas (overage billed). Monitoring
            is best-effort.
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => load()}
            disabled={loading}
            sx={{
              letterSpacing: "0.14em",
              textTransform: "uppercase",
              borderColor: JarvisTheme.colors.accentBorder,
              color: JarvisTheme.colors.muted,
              "&:hover": { borderColor: JarvisTheme.colors.accent },
            }}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !data ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress
            size={22}
            sx={{ color: JarvisTheme.colors.accent }}
          />
        </Box>
      ) : !data ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No snapshot loaded yet. Hit Refresh to fetch metrics.
        </Alert>
      ) : (
        <>
          <Typography
            sx={{
              fontFamily: JarvisTheme.fonts.mono,
              fontSize: "0.75rem",
              color: JarvisTheme.colors.muted,
              opacity: 0.75,
              mb: 2,
              wordBreak: "break-word",
            }}
          >
            Project: {data.projectId} · Bucket: {data.bucket ?? "—"} · Updated:{" "}
            {new Date(data.generatedAt).toLocaleString()}
          </Typography>

          <Box
            sx={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: {
                xs: "minmax(0, 1fr)",
                sm: "repeat(2, minmax(0, 1fr))",
                md: "repeat(3, minmax(0, 1fr))",
              },
              gap: 2,
              alignItems: "stretch",
              boxSizing: "border-box",
              maxWidth: "100%",
            }}
          >
            {sortedStatTiles.map(({ id, node }) => (
              <Box key={id} sx={{ minWidth: 0 }}>
                {node}
              </Box>
            ))}
          </Box>

          {data.gemini && (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                borderRadius: 1.25,
                border: `1px solid ${JarvisTheme.colors.accentBorder}`,
                bgcolor: JarvisTheme.colors.panelSoft,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: 1,
                  mb: 2,
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontFamily: JarvisTheme.fonts.ui,
                      letterSpacing: "0.16em",
                      textTransform: "uppercase",
                      fontSize: "0.72rem",
                      color: JarvisTheme.colors.muted,
                      mb: 0.5,
                    }}
                  >
                    Gemini API · {data.gemini.month}
                  </Typography>
                  <Typography
                    sx={{
                      fontFamily: JarvisTheme.fonts.mono,
                      fontSize: { xs: "1.35rem", sm: "1.5rem" },
                      color: JarvisTheme.colors.accent,
                    }}
                  >
                    {fmtUsd(data.gemini.totalEstimatedCostUsd)}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "0.82rem",
                      color: JarvisTheme.colors.muted,
                      mt: 0.5,
                    }}
                  >
                    {fmtNumber(data.gemini.totalRequests)} requests ·{" "}
                    {fmtNumber(data.gemini.totalInputTokens)} in /{" "}
                    {fmtNumber(data.gemini.totalOutputTokens)} out tokens
                  </Typography>
                </Box>
                <Link
                  href={data.gemini.aiStudioUsageUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontFamily: JarvisTheme.fonts.ui,
                    fontSize: "0.8rem",
                    color: JarvisTheme.colors.accent,
                    letterSpacing: "0.08em",
                    textTransform: "uppercase",
                  }}
                >
                  AI Studio billing →
                </Link>
              </Box>

              <Typography
                sx={{
                  fontSize: "0.78rem",
                  color: JarvisTheme.colors.muted,
                  mb: 2,
                  opacity: 0.85,
                }}
              >
                {data.gemini.pricingNote}
                {data.gemini.updatedAt
                  ? ` Last logged: ${new Date(data.gemini.updatedAt).toLocaleString()}.`
                  : ""}
              </Typography>

              {data.gemini.byModel.length > 0 ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Table
                    size="small"
                    sx={{
                      "& .MuiTableCell-root": {
                        fontFamily: JarvisTheme.fonts.mono,
                        fontSize: "0.75rem",
                        color: JarvisTheme.colors.text,
                        borderColor: JarvisTheme.colors.panelBorder,
                      },
                      "& .MuiTableCell-head": {
                        fontFamily: JarvisTheme.fonts.ui,
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        fontSize: "0.65rem",
                        color: JarvisTheme.colors.muted,
                      },
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Model</TableCell>
                        <TableCell align="right">Requests</TableCell>
                        <TableCell align="right">Input</TableCell>
                        <TableCell align="right">Output</TableCell>
                        <TableCell align="right">Est. cost</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {data.gemini.byModel.map((row) => (
                        <TableRow key={row.model}>
                          <TableCell>{row.model}</TableCell>
                          <TableCell align="right">
                            {fmtNumber(row.requestCount)}
                          </TableCell>
                          <TableCell align="right">
                            {fmtNumber(row.inputTokens)}
                          </TableCell>
                          <TableCell align="right">
                            {fmtNumber(row.outputTokens)}
                          </TableCell>
                          <TableCell align="right">
                            {fmtUsd(row.estimatedCostUsd)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.85rem",
                    color: JarvisTheme.colors.muted,
                    opacity: 0.75,
                  }}
                >
                  No Gemini usage logged this month yet.
                </Typography>
              )}

              {data.gemini.notes.map((n, i) => (
                <Alert key={`gemini-${i}`} severity="info" sx={{ mt: 1.5 }}>
                  {n}
                </Alert>
              ))}
            </Paper>
          )}

          {(data.functions.notes.length > 0 ||
            data.firestore.notes.length > 0 ||
            data.hosting.notes.length > 0 ||
            data.storage.notes.length > 0) && (
            <Box sx={{ mt: 2 }}>
              {data.functions.notes.map((n, i) => (
                <Alert key={`fn-${i}`} severity="warning" sx={{ mb: 1 }}>
                  {n}
                </Alert>
              ))}
              {data.firestore.notes.map((n, i) => (
                <Alert key={`fs-${i}`} severity="warning" sx={{ mb: 1 }}>
                  {n}
                </Alert>
              ))}
              {data.hosting.notes.map((n, i) => (
                <Alert key={`host-${i}`} severity="warning" sx={{ mb: 1 }}>
                  {n}
                </Alert>
              ))}
              {data.storage.notes.map((n, i) => (
                <Alert key={`st-${i}`} severity="warning" sx={{ mb: 1 }}>
                  {n}
                </Alert>
              ))}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
